import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Clock, UserCheck, MapPin, Search, Loader, Eye, XCircle, Phone, SlidersHorizontal, UserMinus } from 'lucide-react';
import api, { ASSET_BASE } from '../../utils/api';
import { io } from "socket.io-client"

const ScheduleTrips = () => {
  const [pendingTrips, setPendingTrips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedDriverLogs, setSelectedDriverLogs] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [isDriversLoading, setIsDriversLoading] = useState(false);
  const [searchDriver, setSearchDriver] = useState('');
  const [sortOption, setSortOption] = useState('priorityScore');
  const [assigningState, setAssigningState] = useState({});
  const [cancellingState, setCancellingState] = useState({});
  const [dispatchedState, setDispatchedState] = useState({});
  const [visibleTiers, setVisibleTiers] = useState(1);

  // Manual tier addition states
  const [isSearchDriverModalOpen, setIsSearchDriverModalOpen] = useState(false);
  const [targetTierForAdd, setTargetTierForAdd] = useState(null);
  const [overallDriversSearch, setOverallDriversSearch] = useState('');
  const [overallDriversResults, setOverallDriversResults] = useState([]);
  const [isSearchingOverall, setIsSearchingOverall] = useState(false);
  const [manualTierAdditions, setManualTierAdditions] = useState({ 1: [], 2: [], 3: [] });

  // Existing drivers modal states
  const [isExistingDriversModalOpen, setIsExistingDriversModalOpen] = useState(false);
  const [existingDrivers, setExistingDrivers] = useState([]);
  const [existingDriversSearch, setExistingDriversSearch] = useState('');
  const [isLoadingExistingDrivers, setIsLoadingExistingDrivers] = useState(false);
  const [existingDriverAssigningState, setExistingDriverAssigningState] = useState({});

  const socketRef = useRef(null);

  useEffect(() => {
    fetchPendingTrips();
  }, []);

  useEffect(() => {
    // ASSET_BASE is a full origin in dev ("http://localhost:5000") but a path prefix
    // in production ("/notdashboard"). socket.io reads a bare path argument as a
    // namespace, so pass the prefix as the transport path instead of as a URL.
    socketRef.current = /^https?:/.test(ASSET_BASE)
      ? io(ASSET_BASE)
      : io({ path: `${ASSET_BASE}/socket.io` });

    socketRef.current.on("trip_assigned", (data) => {
      // If we are assigning a driver from this modal, close it
      if (
        isModalOpen &&
        selectedTrip &&
        (selectedTrip._id === data.createTripId || selectedTrip._id === data.tripId)
      ) {
        setIsModalOpen(false);
        setSelectedTrip(null);
      }

      // REMOVE CUSTOMER/TRIP
      setPendingTrips((prev) =>
        prev.filter((trip) => trip._id !== (data.createTripId || data.tripId)),
      );

      // REMOVE ACCEPTED DRIVER
      setAvailableDrivers((prev) =>
        prev.filter((driver) => driver._id !== data.driverId),
      );
    });

    socketRef.current.on("trip_dispatch_failed", (data) => {
      toast.error("Auto dispatch failed. No drivers accepted the trip.");
      fetchPendingTrips();
    });

    socketRef.current.on("trip_dispatch_cancelled", (data) => {
      toast.success("Dispatch cancelled.");
      fetchPendingTrips();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [isModalOpen, selectedTrip]);

  const fetchPendingTrips = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/trips/acting-driver/pending');
      if (res.data && res.data.success) {
        setPendingTrips(res.data.data || []);
      }
    } catch (err) {
      toast.error('Failed to load pending trips.');
    } finally {
      setIsLoading(false);
    }
  };

  const openAssignModal = async (trip) => {
    setSelectedTrip(trip);
    setIsModalOpen(true);
    setSearchDriver('');
    setSortOption('priorityScore');
    setManualTierAdditions({ 1: [], 2: [], 3: [] });
    try {
      setIsDriversLoading(true);
      const res = await api.get(`/drivers/available-acting?tripId=${trip._id}`);
      if (res.data && res.data.success) {
        setAvailableDrivers(res.data.data || []);
        if (!res.data.data || res.data.data.length === 0) {
          toast('No available acting drivers found.');
        }
      } else {
        toast.error('Invalid response from server.');
      }
    } catch (err) {
      console.error('Error fetching available drivers:', err);
      toast.error(err.response?.data?.message || 'Failed to load available acting drivers.');
    } finally {
      setIsDriversLoading(false);
      setVisibleTiers(1);
    }
  };

  const openDetailsModal = (trip) => {
    setSelectedTrip(trip);
    setIsDetailsModalOpen(true);
  };

  const openRouteModal = (trip) => {
    setSelectedTrip(trip);
    setIsRouteModalOpen(true);
  };

  const handleAssignDriver = async (specificDrivers = null, label = null, actionKey = "all") => {
    if (!selectedTrip) return;

    const driversToAssign = specificDrivers || availableDrivers;

    if (!driversToAssign || driversToAssign.length === 0) {
      toast.error("No drivers selected to assign.");
      return;
    }

    const stateKey = `${selectedTrip._id}_${actionKey}`;

    try {
      setAssigningState(prev => ({ ...prev, [stateKey]: true }));
      const isSingleManual = specificDrivers?.length === 1;
      const res = await api.post('/trips/acting-driver/assign', {
        tripId: selectedTrip._id,
        drivers: driversToAssign.map(driver => ({
          driverId: driver._id,
          priorityScore: driver.priorityScore,
          timelineLogs: driver.timelineLogs
        })),
        dispatchLabel: label || (isSingleManual ? "Manual Assign" : "Auto Dispatch"),
        forceAssign: isSingleManual
      });
      if (res.data && res.data.success) {
        // `offered` means the matching engine is ringing the driver and is now
        // waiting on their answer — the trip is not theirs until they accept.
        if (res.data.offered) {
          toast.success(res.data.message || 'Request sent. Waiting for the driver to accept.');
          setSelectedTrip(prev => ({ ...prev, status: "DISPATCHING" }));
        }

        if (actionKey === "all" || actionKey.startsWith("tier")) {
          setDispatchedState(prev => ({ ...prev, [stateKey]: true }));
          setSelectedTrip(prev => ({ ...prev, status: "DISPATCHING" }));
        } else {
          setIsModalOpen(false);
        }

        fetchPendingTrips();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign driver.');
    } finally {
      setAssigningState(prev => ({ ...prev, [stateKey]: false }));
    }
  };

  const handleCancelDispatch = async (trip, actionKey = "main", specificDrivers = null) => {
    const stateKey = `${trip._id}_${actionKey}`;
    try {
      setCancellingState(prev => ({ ...prev, [stateKey]: true }));
      const res = await api.post('/trips/acting-driver/cancel-dispatch', {
        tripId: trip.mainTripId || trip._id,
        createTripId: trip._id,
        driversToRemove: specificDrivers ? specificDrivers.map(d => d._id) : null
      });
      if (res.data && res.data.success) {
        if (res.data.partial) {
          setDispatchedState(prev => {
            const newState = { ...prev };
            delete newState[stateKey];
            return newState;
          });
        } else {
          setDispatchedState(prev => {
            const newState = { ...prev };
            Object.keys(newState).forEach(k => {
              if (k.startsWith(`${trip._id}_`)) delete newState[k];
            });
            return newState;
          });

          if (selectedTrip && selectedTrip._id === trip._id) {
            setSelectedTrip(prev => ({ ...prev, status: "PENDING" }));
          }
        }
        fetchPendingTrips();
      }
    } catch (err) {
      console.error('Cancel dispatch error:', err);
      toast.error(err.response?.data?.message || 'Failed to cancel dispatch.');
    } finally {
      setCancellingState(prev => ({ ...prev, [stateKey]: false }));
    }
  };

  const getSortedDrivers = () => {
    return [...availableDrivers].sort((a, b) => {
      if (sortOption === 'priorityScore') return b.priorityScore - a.priorityScore;

      const scoreA = a.individualScores?.[sortOption] || 0;
      const scoreB = b.individualScores?.[sortOption] || 0;
      return scoreB - scoreA;
    });
  };

  const filteredDrivers = getSortedDrivers().filter(d =>
    d.name?.toLowerCase().includes(searchDriver.toLowerCase()) ||
    d.phone?.includes(searchDriver) ||
    d.licenseNo?.toLowerCase().includes(searchDriver.toLowerCase())
  );

  const handleRemoveDriver = (driverId) => {
    setAvailableDrivers(prev => prev.filter(d => d._id !== driverId));
    setManualTierAdditions(prev => ({
      1: prev[1].filter(d => d._id !== driverId),
      2: prev[2].filter(d => d._id !== driverId),
      3: prev[3].filter(d => d._id !== driverId),
    }));
  };

  const handleSearchOverallDrivers = async (query) => {
    setOverallDriversSearch(query);
    if (!query || query.length < 2) {
      setOverallDriversResults([]);
      return;
    }
    setIsSearchingOverall(true);
    try {
      const res = await api.get(`/drivers?search=${query}&limit=10`);
      if (res.data && res.data.success && res.data.data.drivers) {
        setOverallDriversResults(res.data.data.drivers);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to search overall drivers');
    } finally {
      setIsSearchingOverall(false);
    }
  };

  const handleAddDriverToTier = (driver) => {
    const isAlreadyInTier = manualTierAdditions[targetTierForAdd].some(d => d._id === driver._id);
    if (isAlreadyInTier) {
      toast("Driver is already in this tier.");
      return;
    }

    const driverToAdd = {
      ...driver,
      priorityScore: driver.priorityScore || 100,
      individualScores: driver.individualScores || { manual: 100 },
      timelineLogs: driver.timelineLogs || ["Manually added to tier by Admin"],
      isManualAddition: true
    };

    setManualTierAdditions(prev => ({
      ...prev,
      [targetTierForAdd]: [...prev[targetTierForAdd], driverToAdd]
    }));

    setIsSearchDriverModalOpen(false);
    setOverallDriversSearch('');
    setOverallDriversResults([]);
    toast.success(`${driver.name} added to Tier ${targetTierForAdd}`);
  };

  const openAddDriverToTierModal = (tierIndex) => {
    setTargetTierForAdd(tierIndex);
    setOverallDriversSearch('');
    setOverallDriversResults([]);
    setIsSearchDriverModalOpen(true);
  };

  const openExistingDriversModal = async () => {
    setIsExistingDriversModalOpen(true);
    setExistingDriversSearch('');
    setExistingDriverAssigningState({});
    setIsLoadingExistingDrivers(true);
    try {
      const res = await api.get('/drivers?limit=50&isApproved=true&actingOnly=true');
      if (res.data && res.data.success) {
        setExistingDrivers(res.data.data?.drivers || []);
      }
    } catch (err) {
      toast.error('Failed to load drivers.');
    } finally {
      setIsLoadingExistingDrivers(false);
    }
  };

  const handleExistingDriversSearch = async (query) => {
    setExistingDriversSearch(query);
    setIsLoadingExistingDrivers(true);
    try {
      const params = query.trim() ? `&search=${query}` : '';
      const res = await api.get(`/drivers?limit=50&isApproved=true&actingOnly=true${params}`);
      if (res.data && res.data.success) {
        setExistingDrivers(res.data.data?.drivers || []);
      }
    } catch (err) {
      toast.error('Failed to search drivers.');
    } finally {
      setIsLoadingExistingDrivers(false);
    }
  };

  const handleAssignExistingDriver = async (driver) => {
    if (!selectedTrip) return;
    setExistingDriverAssigningState(prev => ({ ...prev, [driver._id]: true }));
    try {
      const res = await api.post('/trips/acting-driver/assign', {
        tripId: selectedTrip._id,
        drivers: [{ driverId: driver._id, priorityScore: 100, timelineLogs: ['Manually assigned from existing drivers'] }],
        dispatchLabel: `Manual Assignment: ${driver.name}`,
        forceAssign: true
      });
      if (res.data && res.data.success) {
        // Engine path rings the driver and waits for them; only the fallback
        // path assigns outright. Say which actually happened.
        toast.success(
          res.data.message ||
          (res.data.offered
            ? `Request sent to ${driver.name}. Waiting for them to accept.`
            : `${driver.name} assigned successfully.`)
        );
        setIsExistingDriversModalOpen(false);
        setIsModalOpen(false);
        fetchPendingTrips();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign driver.');
    } finally {
      setExistingDriverAssigningState(prev => ({ ...prev, [driver._id]: false }));
    }
  };

  const getTieredDrivers = () => {
    const allManualIds = [
      ...manualTierAdditions[1],
      ...manualTierAdditions[2],
      ...manualTierAdditions[3]
    ].map(d => d._id);

    const drivers = filteredDrivers.filter(d => !allManualIds.includes(d._id));
    const n = drivers.length;

    if (n <= 5) {
      return {
        tier1: [...manualTierAdditions[1], ...drivers],
        tier2: [...manualTierAdditions[2]],
        tier3: [...manualTierAdditions[3]]
      };
    }

    const count1 = Math.max(5, Math.round(n * 0.3));
    const tier1 = drivers.slice(0, count1);

    const remainingAfter1 = drivers.slice(count1);
    const nRemaining1 = remainingAfter1.length;
    const count2 = Math.round(nRemaining1 * 0.6);

    const tier2 = remainingAfter1.slice(0, count2);
    const tier3 = remainingAfter1.slice(count2);

    return {
      tier1: [...manualTierAdditions[1], ...tier1],
      tier2: [...manualTierAdditions[2], ...tier2],
      tier3: [...manualTierAdditions[3], ...tier3]
    };
  };

  const { tier1, tier2, tier3 } = getTieredDrivers();

  const DriverRow = ({ driver }) => (
    <tr key={driver._id} className="hover:bg-blue-50/30 transition-colors">
      <td className="px-6 py-5 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-11 w-11 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-lg mr-4 relative">
            {driver.name?.charAt(0)}
            {driver.isManualAddition && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" title="Manually Added"></span>
              </span>
            )}
          </div>
          <div>
            <div className="text-base font-bold text-gray-900">{driver.name}</div>
            <div className="text-sm text-gray-600 flex items-center mt-0.5">
              <a href={`tel:${driver.phone}`} className="flex items-center hover:text-primary-600 transition-colors" title={`Call ${driver.name}`}>
                <Phone className="h-4 w-4 mr-1 text-primary-500" /> {driver.phone}
              </a>
              <span className="mx-2 text-gray-400">|</span>
              {driver.licenseNo}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-5 whitespace-nowrap text-center">
        {driver.isManualAddition ? (
          <div className="text-xl font-black text-blue-600">Manual</div>
        ) : (
          <div className={`text-xl font-black ${driver.priorityScore >= 80 ? 'text-green-600' :
            driver.priorityScore >= 50 ? 'text-yellow-600' : 'text-red-700'
            }`}>
            {driver.priorityScore}%
          </div>
        )}
      </td>
      <td className="px-6 py-5">
        <div className="flex flex-wrap gap-2 items-center">
          {driver.isManualAddition ? (
            <span className="px-2.5 py-1 bg-blue-50 rounded text-sm font-bold text-blue-700 border border-blue-200">
              Manually Included
            </span>
          ) : (
            driver.individualScores && Object.entries(driver.individualScores).map(([k, v]) => (
              <span key={k} className="px-2.5 py-1 bg-gray-100 rounded text-sm font-bold text-gray-700 border capitalize">
                {k}: {v}%
              </span>
            ))
          )}
          <button
            onClick={() => {
              setSelectedDriverLogs(driver.timelineLogs || []);
              setIsLogsModalOpen(true);
            }}
            className="text-xs font-bold text-primary-600 hover:underline ml-2 flex items-center"
          >
            <SlidersHorizontal className="h-3 w-3 mr-1" /> View Reason
          </button>
        </div>
      </td>
      <td className="px-6 py-5 whitespace-nowrap text-right">
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleRemoveDriver(driver._id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
            title="Reject Driver"
          >
            <UserMinus className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleAssignDriver([driver], `Manual Assignment: ${driver.name}`, `driver_${driver._id}`)}
            disabled={assigningState[`${selectedTrip?._id}_driver_${driver._id}`]}
            className="px-6 py-2 bg-primary-600 text-white rounded font-bold text-sm hover:bg-primary-700 shadow transition-all active:scale-95 disabled:opacity-50"
          >
            {assigningState[`${selectedTrip?._id}_driver_${driver._id}`] ? "Assigning..." : "Assign"}
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center space-x-4">
            <div className="p-2 rounded-full bg-primary-100 text-primary-600">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <h1 className="page-title">Schedule Acting Drivers</h1>
              <p className="page-subtitle mt-1">
                Assign available acting drivers to pending trips
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6">
        <div className="card">
          <div className="card-body p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader className="h-8 w-8 animate-spin mx-auto text-primary-600" />
                <p className="mt-2 text-gray-500">Loading pending trips...</p>
              </div>
            ) : pendingTrips.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-lg font-medium">
                  No pending acting-driver trips
                </p>
                <p className="text-sm">
                  Trips requiring acting drivers will appear here.
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Passenger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Route
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingTrips.map((trip) => (
                    <tr key={trip._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {trip.userName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {trip.phoneNumber}
                        </div>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${trip.scheduleType === 'FUTURE' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {trip.scheduleType === 'FUTURE' ? '📅 Scheduled' : '🕐 Today'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-1 text-green-500" />
                              <span
                                className="truncate max-w-[150px]"
                                title={trip.from?.address}
                              >
                                {trip.from?.address || "Unknown"}
                              </span>
                            </div>
                            {trip.stops && trip.stops.length > 0 && (
                              <div className="flex items-center text-xs text-blue-600 pl-5">
                                + {trip.stops.length} stop(s)
                              </div>
                            )}
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-1 text-red-500" />
                              <span
                                className="truncate max-w-[150px]"
                                title={trip.to?.address}
                              >
                                {trip.to?.address || "Unknown"}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => openRouteModal(trip)}
                            className="ml-2 text-gray-400 hover:text-primary-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                            title="View Route Details"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-1 text-green-500" />
                            <span className="truncate max-w-[150px]">
                              {trip.scheduledFrom
                                ? new Date(trip.scheduledFrom).toLocaleString(
                                  [],
                                  {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                                : "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-1 text-red-500" />
                            <span className="truncate max-w-[150px]">
                              {trip.scheduledTo
                                ? new Date(trip.scheduledTo).toLocaleString(
                                  [],
                                  {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {trip.vehicleName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {trip.regNo || "No Reg No"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {new Date(trip.createdAt).toLocaleDateString()}
                          <br />
                          {new Date(trip.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2 items-center">
                          <button
                            onClick={() => openDetailsModal(trip)}
                            className="btn btn-outline btn-sm p-2 rounded-full flex items-center justify-center"
                            title="View Full Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {trip.status === 'DISPATCHING' ? (
                            <button
                              onClick={() => handleCancelDispatch(trip, "main")}
                              disabled={cancellingState[`${trip._id}_main`]}
                              className="btn btn-outline-danger btn-sm flex items-center text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              {cancellingState[`${trip._id}_main`] ? (
                                <Loader className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <XCircle className="h-4 w-4 mr-1" />
                              )}
                              {cancellingState[`${trip._id}_main`] ? 'Stopping...' : 'Stop Dispatch'}
                            </button>
                          ) : (
                            <button
                              onClick={() => openAssignModal(trip)}
                              className="btn btn-primary btn-sm flex items-center"
                            >
                              <UserCheck className="h-4 w-4 mr-1" /> Assign
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Assign Driver Modal */}
      {isModalOpen && selectedTrip && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setIsModalOpen(false)}
            ></div>

            <div className={`
              relative inline-block w-full max-w-6xl p-0 overflow-hidden 
              text-left align-middle transition-all transform bg-white 
              shadow-2xl rounded-none
            `}>
              {/* Header */}
              <div className="bg-gray-800 px-6 py-4 flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <UserCheck className="h-6 w-6 text-primary-400" />
                  <h3 className="text-lg font-bold text-white">Assign Acting Driver</h3>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={openExistingDriversModal}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded font-bold text-sm hover:bg-gray-500 transition-all border border-gray-500"
                  >
                    <Search className="h-4 w-4" />
                    <span>Existing Drivers</span>
                  </button>
                  {dispatchedState[`${selectedTrip?._id}_all`] || (selectedTrip?.status === 'DISPATCHING' && Object.keys(dispatchedState).filter(k => k.startsWith(`${selectedTrip?._id}_`) && dispatchedState[k]).length === 0) ? (
                    <button
                      onClick={() => handleCancelDispatch(selectedTrip, "all")}
                      disabled={cancellingState[`${selectedTrip?._id}_all`]}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded font-bold text-sm hover:bg-red-700 disabled:opacity-50 transition-all"
                    >
                      {cancellingState[`${selectedTrip?._id}_all`] ? <Loader className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                      <span>{cancellingState[`${selectedTrip?._id}_all`] ? "Cancelling..." : "Stop Dispatch"}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAssignDriver(null, "Auto Dispatch All", "all")}
                      disabled={Object.keys(assigningState).some(k => k.startsWith(`${selectedTrip?._id}_`) && assigningState[k]) || Object.keys(dispatchedState).some(k => k.startsWith(`${selectedTrip?._id}_`) && dispatchedState[k]) || availableDrivers.length === 0}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded font-bold text-sm hover:bg-primary-700 disabled:opacity-50 transition-all"
                    >
                      {assigningState[`${selectedTrip?._id}_all`] ? <Loader className="h-4 w-4 animate-spin" /> : <SlidersHorizontal className="h-4 w-4" />}
                      <span>{assigningState[`${selectedTrip?._id}_all`] ? "Dispatching..." : "Start Auto Dispatch All"}</span>
                    </button>
                  )}
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Compact Trip Summary Bar */}
              <div className="bg-gray-100 px-6 py-4 border-b grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Trip Details</span>
                  <div className="text-base font-bold text-gray-900">
                    <p>{selectedTrip.userName} ({selectedTrip.phoneNumber})</p>
                    <p className="text-gray-600 text-sm mt-1">{selectedTrip.vehicleName} · {selectedTrip.regNo || "N/A"}</p>
                  </div>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Schedule</span>
                  <div className="text-sm font-bold text-gray-800 space-y-1">
                    <div className="flex items-center text-green-700">
                      <Clock className="h-4 w-4 mr-1.5" />
                      <span>Start: {new Date(selectedTrip.scheduledFrom).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center text-red-700">
                      <Clock className="h-4 w-4 mr-1.5" />
                      <span>End: {new Date(selectedTrip.scheduledTo).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Route</span>
                  <div className="space-y-1.5">
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-1.5 text-green-600 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-tight">{selectedTrip.from?.address}</p>
                    </div>
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-1.5 text-red-600 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-tight">{selectedTrip.to?.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="px-6 py-3 bg-white border-b flex justify-between items-center">
                <div className="relative w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type="text"
                    className="w-full pl-9 pr-4 py-2 border rounded text-sm focus:ring-1 focus:ring-primary-500"
                    placeholder="Filter drivers by name, phone or license..."
                    value={searchDriver}
                    onChange={(e) => setSearchDriver(e.target.value)}
                  />
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs font-bold text-gray-500 uppercase">Sort By:</span>
                  <select
                    className="border rounded px-3 py-1.5 text-sm font-medium text-gray-700 focus:ring-1 focus:ring-primary-500"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <option value="priorityScore">Priority Score</option>
                    <option value="experience">Experience</option>
                    <option value="rating">Rating</option>
                    <option value="idleTime">Idle Time</option>
                  </select>
                </div>
              </div>

              {/* Driver Table Tiers */}
              <div className="overflow-y-auto max-h-[600px] bg-gray-50">
                {isDriversLoading ? (
                  <div className="px-6 py-20 text-center bg-white">
                    <Loader className="h-8 w-8 animate-spin mx-auto text-primary-600 mb-2" />
                    <p className="text-sm text-gray-500">Scanning available drivers...</p>
                  </div>
                ) : filteredDrivers.length === 0 ? (
                  <div className="px-6 py-20 text-center text-gray-500 bg-white">
                    No matching acting drivers found for this trip.
                  </div>
                ) : (
                  <div className="space-y-6 p-6">
                    {/* Tier 1 */}
                    <div className="bg-white rounded-none shadow-sm border overflow-hidden">
                      <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
                        <h4 className="text-white font-bold flex items-center">
                          <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full mr-2">1</span>
                          Priority Tier (Top 20-30%)
                        </h4>
                        <div className="flex space-x-3 items-center">
                          <button
                            onClick={() => openAddDriverToTierModal(1)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-gray-700 text-white border border-gray-600 rounded font-bold text-xs hover:bg-gray-600 transition-all"
                          >
                            <UserCheck className="h-3 w-3" />
                            <span>Add Driver</span>
                          </button>
                          {dispatchedState[`${selectedTrip?._id}_tier1`] ? (
                            <button
                              onClick={() => handleCancelDispatch(selectedTrip, "tier1", tier1)}
                              disabled={cancellingState[`${selectedTrip?._id}_tier1`]}
                              className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 text-white rounded font-bold text-xs hover:bg-red-700 disabled:opacity-50 transition-all"
                            >
                              {cancellingState[`${selectedTrip?._id}_tier1`] ? <Loader className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                              <span>{cancellingState[`${selectedTrip?._id}_tier1`] ? "Cancelling..." : "Cancel Tier 1"}</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAssignDriver(tier1, "Auto Dispatch Tier 1", "tier1")}
                              disabled={assigningState[`${selectedTrip?._id}_tier1`] || assigningState[`${selectedTrip?._id}_all`] || dispatchedState[`${selectedTrip?._id}_all`] || (selectedTrip?.status === 'DISPATCHING' && Object.keys(dispatchedState).filter(k => k.startsWith(`${selectedTrip?._id}_`) && dispatchedState[k]).length === 0) || tier1.length === 0}
                              className="flex items-center space-x-2 px-3 py-1.5 bg-primary-600 text-white rounded font-bold text-xs hover:bg-primary-700 disabled:opacity-50 transition-all"
                            >
                              {assigningState[`${selectedTrip?._id}_tier1`] ? <Loader className="h-3 w-3 animate-spin" /> : <SlidersHorizontal className="h-3 w-3" />}
                              <span>{assigningState[`${selectedTrip?._id}_tier1`] ? "Dispatching..." : "Auto Dispatch Tier 1"}</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-[300px]">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Driver</th>
                              <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Priority</th>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Score</th>
                              <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {tier1.map(driver => <DriverRow key={driver._id} driver={driver} />)}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Tier 2 */}
                    {tier2.length > 0 && (
                      <div className="flex justify-center mt-4 mb-2">
                        <button
                          onClick={() => setVisibleTiers(prev => prev >= 2 ? 1 : 2)}
                          className="px-6 py-2 bg-white border border-primary-600 text-primary-600 rounded-md font-bold text-sm hover:bg-primary-50 transition-all flex items-center shadow-sm"
                        >
                          {visibleTiers >= 2 ? <XCircle className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                          {visibleTiers >= 2 ? 'Hide Secondary Tier' : 'Show More (Next 50-60%)'}
                        </button>
                      </div>
                    )}

                    {visibleTiers >= 2 && tier2.length > 0 && (
                      <div className="bg-white rounded-none shadow-sm border overflow-hidden">
                        <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
                          <h4 className="text-white font-bold flex items-center">
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full mr-2">2</span>
                            Secondary Tier (Next 50-60%)
                          </h4>
                          <div className="flex space-x-3 items-center">
                            <button
                              onClick={() => openAddDriverToTierModal(2)}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 text-white border border-gray-500 rounded font-bold text-xs hover:bg-gray-500 transition-all"
                            >
                              <UserCheck className="h-3 w-3" />
                              <span>Add Driver</span>
                            </button>
                            {dispatchedState[`${selectedTrip?._id}_tier2`] ? (
                              <button
                                onClick={() => handleCancelDispatch(selectedTrip, "tier2", tier2)}
                                disabled={cancellingState[`${selectedTrip?._id}_tier2`]}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 text-white rounded font-bold text-xs hover:bg-red-700 disabled:opacity-50 transition-all"
                              >
                                {cancellingState[`${selectedTrip?._id}_tier2`] ? <Loader className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                                <span>{cancellingState[`${selectedTrip?._id}_tier2`] ? "Cancelling..." : "Cancel Tier 2"}</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAssignDriver(tier2, "Auto Dispatch Tier 2", "tier2")}
                                disabled={assigningState[`${selectedTrip?._id}_tier2`] || assigningState[`${selectedTrip?._id}_all`] || dispatchedState[`${selectedTrip?._id}_all`] || (selectedTrip?.status === 'DISPATCHING' && Object.keys(dispatchedState).filter(k => k.startsWith(`${selectedTrip?._id}_`) && dispatchedState[k]).length === 0) || tier2.length === 0}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-primary-600 text-white rounded font-bold text-xs hover:bg-primary-700 disabled:opacity-50 transition-all"
                              >
                                {assigningState[`${selectedTrip?._id}_tier2`] ? <Loader className="h-3 w-3 animate-spin" /> : <SlidersHorizontal className="h-3 w-3" />}
                                <span>{assigningState[`${selectedTrip?._id}_tier2`] ? "Dispatching..." : "Auto Dispatch Tier 2"}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-[300px]">
                          <table className="min-w-full divide-y divide-gray-200">
                            <tbody className="bg-white divide-y divide-gray-200">
                              {tier2.map(driver => <DriverRow key={driver._id} driver={driver} />)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Tier 3 */}
                    {visibleTiers >= 2 && tier3.length > 0 && (
                      <div className="flex justify-center mt-4 mb-2">
                        <button
                          onClick={() => setVisibleTiers(prev => prev >= 3 ? 2 : 3)}
                          className="px-6 py-2 bg-white border border-primary-600 text-primary-600 rounded-md font-bold text-sm hover:bg-primary-50 transition-all flex items-center shadow-sm"
                        >
                          {visibleTiers >= 3 ? <XCircle className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                          {visibleTiers >= 3 ? 'Hide Final Tier' : 'Show Remaining Drivers'}
                        </button>
                      </div>
                    )}

                    {visibleTiers >= 3 && tier3.length > 0 && (
                      <div className="bg-white rounded-none shadow-sm border overflow-hidden">
                        <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
                          <h4 className="text-white font-bold flex items-center">
                            <span className="bg-gray-500 text-white text-xs px-2 py-0.5 rounded-full mr-2">3</span>
                            Final Tier (Remaining 10-20%)
                          </h4>
                          <div className="flex space-x-3 items-center">
                            <button
                              onClick={() => openAddDriverToTierModal(3)}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-gray-500 text-white border border-gray-400 rounded font-bold text-xs hover:bg-gray-400 transition-all"
                            >
                              <UserCheck className="h-3 w-3" />
                              <span>Add Driver</span>
                            </button>
                            {dispatchedState[`${selectedTrip?._id}_tier3`] ? (
                              <button
                                onClick={() => handleCancelDispatch(selectedTrip, "tier3", tier3)}
                                disabled={cancellingState[`${selectedTrip?._id}_tier3`]}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 text-white rounded font-bold text-xs hover:bg-red-700 disabled:opacity-50 transition-all"
                              >
                                {cancellingState[`${selectedTrip?._id}_tier3`] ? <Loader className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                                <span>{cancellingState[`${selectedTrip?._id}_tier3`] ? "Cancelling..." : "Cancel Tier 3"}</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAssignDriver(tier3, "Auto Dispatch Tier 3", "tier3")}
                                disabled={assigningState[`${selectedTrip?._id}_tier3`] || assigningState[`${selectedTrip?._id}_all`] || dispatchedState[`${selectedTrip?._id}_all`] || (selectedTrip?.status === 'DISPATCHING' && Object.keys(dispatchedState).filter(k => k.startsWith(`${selectedTrip?._id}_`) && dispatchedState[k]).length === 0) || tier3.length === 0}
                                className="flex items-center space-x-2 px-3 py-1.5 bg-primary-600 text-white rounded font-bold text-xs hover:bg-primary-700 disabled:opacity-50 transition-all"
                              >
                                {assigningState[`${selectedTrip?._id}_tier3`] ? <Loader className="h-3 w-3 animate-spin" /> : <SlidersHorizontal className="h-3 w-3" />}
                                <span>{assigningState[`${selectedTrip?._id}_tier3`] ? "Dispatching..." : "Auto Dispatch Tier 3"}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-[300px]">
                          <table className="min-w-full divide-y divide-gray-200">
                            <tbody className="bg-white divide-y divide-gray-200">
                              {tier3.map(driver => <DriverRow key={driver._id} driver={driver} />)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Route Details Modal */}
      {isRouteModalOpen && selectedTrip && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setIsRouteModalOpen(false)}
            ></div>

            <div className="relative inline-block w-full max-w-lg p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-bold leading-6 text-gray-900 flex items-center">
                  <MapPin className="h-6 w-6 mr-2 text-primary-600" />
                  Route Details
                </h3>
                <button
                  onClick={() => setIsRouteModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="space-y-4 ml-2 border-l-2 border-gray-300 pl-4 relative">
                    <div className="relative">
                      <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow"></div>
                      <p className="font-semibold text-gray-900 text-sm">
                        Start
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedTrip.from?.address}
                      </p>
                    </div>

                    {selectedTrip.stops?.map((stop, index) => (
                      <div key={index} className="relative">
                        <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
                        <p className="font-semibold text-gray-900 text-sm">
                          Stop {index + 1}
                        </p>
                        <p className="text-sm text-gray-600">{stop.address}</p>
                      </div>
                    ))}

                    <div className="relative">
                      <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow"></div>
                      <p className="font-semibold text-gray-900 text-sm">
                        Destination
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedTrip.to?.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {isDetailsModalOpen && selectedTrip && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setIsDetailsModalOpen(false)}
            ></div>

            <div className="relative inline-block w-full max-w-2xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-bold leading-6 text-gray-900 flex items-center">
                  <MapPin className="h-6 w-6 mr-2 text-primary-600" />
                  Trip Details
                </h3>
                <button
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    Route Information
                  </h4>
                  <div className="space-y-4 ml-2 border-l-2 border-gray-300 pl-4 relative">
                    <div className="relative">
                      <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow"></div>
                      <p className="font-semibold text-gray-900 text-sm">
                        Start
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedTrip.from?.address}
                      </p>
                    </div>

                    {selectedTrip.stops?.map((stop, index) => (
                      <div key={index} className="relative">
                        <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
                        <p className="font-semibold text-gray-900 text-sm">
                          Stop {index + 1}
                        </p>
                        <p className="text-sm text-gray-600">{stop.address}</p>
                      </div>
                    ))}

                    <div className="relative">
                      <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow"></div>
                      <p className="font-semibold text-gray-900 text-sm">
                        Destination
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedTrip.to?.address}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Passenger Information
                  </h4>
                  <p className="text-sm text-gray-600">
                    <strong>Name:</strong> {selectedTrip.userName}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Phone:</strong> {selectedTrip.phoneNumber}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Vehicle Information
                  </h4>
                  <p className="text-sm text-gray-600">
                    <strong>Vehicle:</strong> {selectedTrip.vehicleName}{" "}
                    {selectedTrip.regNo ? `(${selectedTrip.regNo})` : ""}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Type:</strong> {selectedTrip.vehicleType || "N/A"} |{" "}
                    <strong>Make:</strong> {selectedTrip.make || "N/A"}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    openAssignModal(selectedTrip);
                  }}
                  className="btn btn-primary flex items-center"
                >
                  <UserCheck className="h-4 w-4 mr-1" /> Assign Driver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Driver Logs Modal */}
      {isLogsModalOpen && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setIsLogsModalOpen(false)}></div>
            <div className={`
              relative bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 
              overflow-hidden transform transition-all animate-in fade-in 
              zoom-in duration-200
            `}>
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <div className="flex items-center space-x-3">
                  <SlidersHorizontal className="h-6 w-6 text-primary-600" />
                  <h3 className="text-xl font-bold text-gray-900">Driver Matching Reasoning</h3>
                </div>
                <button onClick={() => setIsLogsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-8 h-8" />
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-3">
                {selectedDriverLogs.length > 0 ? (
                  <ul className="space-y-3">
                    {selectedDriverLogs.map((log, index) => (
                      <li key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="h-2 w-2 rounded-full bg-primary-500 mt-2 shrink-0"></div>
                        <p className="text-sm font-medium text-gray-700 leading-relaxed">{log}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-center text-gray-500 py-10">No detailed logs available for this driver matching.</p>
                )}
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setIsLogsModalOpen(false)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded font-bold hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Existing Drivers Modal */}
      {isExistingDriversModalOpen && selectedTrip && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsExistingDriversModalOpen(false)}></div>
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 overflow-hidden">
              <div className="flex justify-between items-center mb-4 border-b pb-4">
                <div className="flex items-center space-x-3">
                  <UserCheck className="h-6 w-6 text-primary-600" />
                  <h3 className="text-xl font-bold text-gray-900">Assign Existing Driver</h3>
                </div>
                <button onClick={() => setIsExistingDriversModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-8 h-8" />
                </button>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Search by name, phone or license..."
                    value={existingDriversSearch}
                    onChange={(e) => handleExistingDriversSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-lg bg-gray-50">
                {isLoadingExistingDrivers ? (
                  <div className="p-8 text-center">
                    <Loader className="h-6 w-6 animate-spin mx-auto text-primary-600 mb-2" />
                    <p className="text-sm text-gray-500">Loading drivers...</p>
                  </div>
                ) : existingDrivers.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {existingDrivers.map((driver) => (
                      <li key={driver._id} className="p-4 hover:bg-white transition-colors flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold mr-3 shrink-0">
                            {driver.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">{driver.name}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <Phone className="h-3 w-3" /> {driver.phone}
                              {driver.vehicleId?.regNo && (
                                <span className="ml-1">· {driver.vehicleId.regNo}</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {driver.driverStatus?.status === 'online' ? (
                                <span className="text-green-600 font-medium">● Online</span>
                              ) : driver.driverStatus?.status === 'busy' ? (
                                <span className="text-yellow-600 font-medium">● Busy</span>
                              ) : (
                                <span className="text-gray-400">● Offline</span>
                              )}
                              {driver.licenseNo && <span className="ml-2">| {driver.licenseNo}</span>}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAssignExistingDriver(driver)}
                          disabled={existingDriverAssigningState[driver._id]}
                          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-bold hover:bg-primary-700 disabled:opacity-50 transition-colors ml-3 shrink-0"
                        >
                          {existingDriverAssigningState[driver._id] ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : 'Assign'}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p>No drivers found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Driver to Add Modal */}
      {isSearchDriverModalOpen && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsSearchDriverModalOpen(false)}></div>
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 overflow-hidden">
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                <div className="flex items-center space-x-3">
                  <Search className="h-6 w-6 text-primary-600" />
                  <h3 className="text-xl font-bold text-gray-900">Include Driver in Tier {targetTierForAdd}</h3>
                </div>
                <button onClick={() => setIsSearchDriverModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-8 h-8" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Driver Name, Phone, or License</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Search overall drivers..."
                    value={overallDriversSearch}
                    onChange={(e) => handleSearchOverallDrivers(e.target.value)}
                  />
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg bg-gray-50">
                {isSearchingOverall ? (
                  <div className="p-8 text-center">
                    <Loader className="h-6 w-6 animate-spin mx-auto text-primary-600 mb-2" />
                    <p className="text-sm text-gray-500">Searching drivers...</p>
                  </div>
                ) : overallDriversResults.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {overallDriversResults.map((driver) => (
                      <li key={driver._id} className="p-4 hover:bg-white transition-colors flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold mr-3">
                            {driver.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">{driver.name}</p>
                            <p className="text-xs text-gray-500">{driver.phone} | {driver.vehicleId?.regNo || 'No Vehicle'}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddDriverToTier(driver)}
                          className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-md text-sm font-bold hover:bg-primary-100 border border-primary-200 transition-colors"
                        >
                          Add
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : overallDriversSearch.length >= 2 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p>No drivers found matching "{overallDriversSearch}"</p>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <p className="text-sm">Type at least 2 characters to search</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleTrips;
